import {
  Component,
  computed,
  DestroyRef, effect, inject, signal, untracked
} from "@angular/core";
import { isMarkerVisible, JobStateService } from "../job-state.store";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { combineLatest, firstValueFrom, map, Subscription } from "rxjs";
import { CachedProjectService } from "../../../../shared/services/cached-project.service";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { ViewerSettingsService } from "../viewer-settings.store";
import {
  BatchIterationMode,
  BatchOutput,
  BatchScreenshotService,
  BatchTarget,
} from "../batch-screenshot.service";
import { FavJobsService } from "../../../services/fav-jobs.service";
import { Job } from "../../../../models/job";
import { NzFloatButtonModule } from "ng-zorro-antd/float-button";
import { NzCheckboxComponent } from "ng-zorro-antd/checkbox";
import { NzFormModule } from "ng-zorro-antd/form";
import { NzPopoverModule } from "ng-zorro-antd/popover";
import { NzButtonComponent } from "ng-zorro-antd/button";
import { NzColorPickerModule } from "ng-zorro-antd/color-picker";
import { CommonModule } from "@angular/common";
import { GeneralizedHorizon } from "../../../../models/generalizedHorizon";
import { NzDividerComponent } from "ng-zorro-antd/divider";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzRadioModule } from "ng-zorro-antd/radio";
import { NzProgressModule } from "ng-zorro-antd/progress";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzIconModule } from "ng-zorro-antd/icon";

@Component({
  selector: "app-settings-box",
  host: { "(document:keydown.escape)": "drawerVisible = false" },
  templateUrl: "./settings-box.component.html",
  styleUrl: "./settings-box.component.less",
  imports: [
    NzFloatButtonModule,
    ReactiveFormsModule,
    NzCheckboxComponent,
    NzFormModule,
    FormsModule,
    NzPopoverModule,
    NzButtonComponent,
    NzColorPickerModule,
    CommonModule,
    NzDividerComponent,
    NzSelectModule,
    NzRadioModule,
    NzProgressModule,
    NzInputModule,
    NzIconModule
],
})
export class SettingsBoxComponent {
  private jobStateService = inject(JobStateService);
  private _fb = inject(FormBuilder);
  private cachedProjectService = inject(CachedProjectService);
  private destroyRef = inject(DestroyRef);
  private viewerSettings = inject(ViewerSettingsService);

  protected markersVisibilityForm!: FormGroup;
  protected markerKeys: string[] = [];
  private markersSubscription?: Subscription;

  protected drawerVisible = false

  protected markerDepths: { [key: string]: string } = {};

  protected bottomOffset = signal<number>(10);

  // Master Checkbox States
  allChecked = true;
  indeterminate = false;
  
  attrObserver: MutationObserver;
  sliceSettingsHeight: number;
  bottomJobTabsObserver: ResizeObserver;
  jobTabsHeight: number;

  constructor() {
    this.markersVisibilityForm = this._fb.group({});
    // re-runs on selectedLog changed
    effect(() => this.rebuildForm());
  }

  // ----- Sonic log marker ---- 
  async rebuildForm() {
    let showMarkers = this.viewerSettings.showWellMarkers();
    let selectedLogId = this.jobStateService.selectedLog()?.id;
    if (this.markersSubscription) this.markersSubscription.unsubscribe();

    if (!selectedLogId || !showMarkers) {
      this.markerKeys = [];
      this.markersVisibilityForm = this._fb.group({});
      return;
    }

    const controls: any = {};
    // this.markerKeys = this.jobStateService.
    let markers = await firstValueFrom(
      this.cachedProjectService.getSonicLog(selectedLogId).pipe(
        map((res: any) => {
          return res.markers || [];
        }),
      ),
    );

    this.markerKeys = markers.map((marker: { z: number; m: string }) => {
      // also make the depth object for the markers
      this.markerDepths[marker.m] = marker.z.toFixed(2);
      return marker.m;
    });
    // construct controls for all marker iwith angular form to be used in template
    const markersVisibility = untracked(() =>
      this.jobStateService.markersVisibility(),
    );
    this.markerKeys.forEach((key) => {
      // markers are visible by default, but a marker the user has already
      // unticked has to come back unticked - the viewer still has it hidden
      controls[key] = new FormControl(isMarkerVisible(markersVisibility, key));
    });

    this.markersVisibilityForm = this._fb.group(controls);
    this.updateMasterCheckboxState();
    this.markersSubscription = this.markersVisibilityForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (values) => {
          if (values) {
            this.updateMasterCheckboxState();
            this.jobStateService.markersVisibility.set(values);
          }
        },
      });
  }

  updateMasterCheckboxState(): void {
    const values = Object.values(this.markersVisibilityForm.value);
    const allTrue = values.every((v) => v === true);
    const allFalse = values.every((v) => v === false);

    this.allChecked = allTrue;
    // Indeterminate is true if some are true and some are false
    this.indeterminate = !allTrue && !allFalse;
  }

  onAllChecked(checked: boolean): void {
    const update: any = {};
    this.markerKeys.forEach((key) => {
      update[key] = checked;
    });
    // emitEvent: true will trigger the valueChanges subscription above
    this.markersVisibilityForm.patchValue(update);
  }


  // ----- Horizon Color Pickers -----
  protected selectedHorizons = this.jobStateService.selectedHorizon;

  updateHorizonColor(horizonId: string, color: string): void {
    this.jobStateService.selectedHorizon.update((horizons) =>
      horizons.map((h) => (h.id === horizonId ? { ...h, color } : h)),
    );
  }

  // ----- Batch Screenshots -----
  protected batchService = inject(BatchScreenshotService);
  protected favJobsService = inject(FavJobsService);

  protected projectJobs = toSignal(
    this.cachedProjectService.currentProject.pipe(map((p) => p?.jobs ?? [])),
    { initialValue: [] as Job[] },
  );

  protected batchOpen = false;
  protected batchJobIds: string[] = [];
  protected batchModels: string[] = [];
  protected batchTargets: Record<BatchTarget, boolean> = {
    full: true,
    inline: false,
    crossline: false,
    depth: false,
  };
  protected batchIterationMode: BatchIterationMode = "last";
  protected batchCustomIterations = "";
  protected batchOutput: BatchOutput = "zip";

  protected onBatchOutputChange(output: BatchOutput) {
    // a slide lays out the inline/crossline/depth captures side by side
    if (output === "slides") {
      this.batchTargets = { full: false, inline: true, crossline: !this.is2d, depth: !this.is2d };
    }
  }

  protected get batchSlideCount(): number {
    const request = this.buildBatchRequest();
    if (request.targets.length === 0) return 0;
    return (
      this.batchJobIds.length *
      this.batchService.iterationsPerJob(request) *
      Math.max(request.models.length, 1)
    );
  }

  protected get availableModels() {
    return this.jobStateService.availableModelsResource.value() ?? [];
  }

  protected get currentModelId(): string {
    return this.jobStateService.selectedModel();
  }

  protected get is2d(): boolean {
    return !!this.jobStateService.jobData()?.modelGrid?.is2d;
  }

  protected get batchCount(): number {
    return this.batchJobIds.length * this.batchService.shotsPerJob(this.buildBatchRequest());
  }

  protected get batchPercent(): number {
    const total = this.batchService.total();
    return total ? Math.round((this.batchService.done() / total) * 100) : 0;
  }

  protected useFavourites() {
    this.batchJobIds = this.favJobsService.favJobs().map((j) => j.id);
  }

  protected startBatch() {
    this.batchService.runBatch(this.buildBatchRequest());
  }

  private buildBatchRequest() {
    return {
      jobIds: this.batchJobIds,
      iterationMode: this.batchIterationMode,
      customIterations: this.parseCustomIterations(),
      targets: (Object.keys(this.batchTargets) as BatchTarget[]).filter((t) => this.batchTargets[t]),
      models: this.batchModels,
      output: this.batchOutput,
    };
  }

  private parseCustomIterations(): number[] {
    return this.batchCustomIterations
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => Number.isFinite(n) && n >= 0);
  }
}
